import { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Heading,
  VStack,
  HStack,
  Text,
  Button,
  Divider,
  useToast,
  Skeleton,
  Badge,
  Icon,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
} from '@chakra-ui/react';
import { PageWrapper } from '@/components/layout';
import { SEO } from '@/components/seo';
import {
  Crown,
  ArrowLeft,
  Check,
  ExternalLink,
  RefreshCw,
  Sparkles,
} from 'lucide-react';
import { useRouter, useSearch } from '@tanstack/react-router';
import {
  useGetSubscription,
  useGetSubscriptionPlans,
  useCreateCheckoutSession,
  useCreatePortalSession,
  useInvalidateSubscription,
} from '@/api/queries/useSubscriptionQueries';
import { SubscriptionTier, getTierDisplayName } from '@/types/subscription';
import AppHeader from '@/components/navigation/AppHeader';
import { openExternalCheckout, openCustomerPortal } from '@/lib/externalPurchase';
import { useIdempotencyToken } from '@/hooks/useIdempotencyToken';
import { useIsPremium } from '@/hooks/useFeatureAccess';
import { BILLING_CANCELED_TOAST, BILLING_SUCCESS_TOAST, useBillingReturnToast } from '@/hooks/useBillingReturnToast';

const PLUS_FEATURES = [
  'TasteInsights™',
  'Custom tailored list suggestions from PaireBOT',
  'Unlimited personal lists',
  'Unlimited group lists',
  'Survivor & Ranking voting modes unlocked',
  'Proximity alerts for listed restaurants',
  'Limited PaireBOT access (conversational AI)',
];

const FREE_FEATURES = [
  'TasteMatching™',
  'Up to 3 personal lists',
  '1 group list (Simple voting only)',
  'Individual restaurant notepads',
  'Social connections',
];

/** `true` for both spellings a return param can arrive in (see the `search` cast in `SubscriptionSettings`). */
const isTrueParam = (value: unknown): boolean => value === true || value === 'true';

export function SubscriptionSettings() {
  const toast = useToast();
  const router = useRouter();
  // The value type is `string | boolean`, not `string`: TanStack Router's default search parser JSON-parses
  // every value, so a literal `?success=true` (what Stripe redirects back to) arrives as the BOOLEAN `true`,
  // while a value a `navigate({ search: { success: 'true' } })` call put there arrives as the string 'true'
  // (it is quoted in the URL). Comparing against `'true'` alone never matched a real Stripe redirect.
  const search = useSearch({ strict: false }) as {
    success?: string | boolean;
    canceled?: string | boolean;
    /** The Stripe Customer Portal's `return_url` param (set by the backend, D51f Phase 1). */
    portal_return?: string | boolean;
  };
  const invalidateSubscription = useInvalidateSubscription();

  const { data: subscription, isLoading: subscriptionLoading } = useGetSubscription();
  const { data: plans, isLoading: plansLoading } = useGetSubscriptionPlans();
  const checkoutMutation = useCreateCheckoutSession();
  const portalMutation = useCreatePortalSession();

  const [selectedTier, setSelectedTier] = useState<SubscriptionTier>(SubscriptionTier.PLUS);
  const getIdempotencyToken = useIdempotencyToken();

  const isLoading = subscriptionLoading || plansLoading;
  // Tier AND standing, the same rule every other surface uses (`useIsPremium`, the D33 Canonical Rule): a member can
  // carry tier PLUS with a status that no longer entitles them (the API maps `unpaid` / `paused` /
  // `incomplete_expired` to `canceled` and keeps the tier), and tier alone would show them the Paire+ card, the
  // renewal date and the benefits list while hiding the Upgrade card, on the one screen that disagreed with the locks.
  const { isPremium } = useIsPremium();
  // The billing portal is a separate question from "premium": it is where a member fixes a failed card, pays an open
  // invoice or reads invoice history, and a lapsed member (tier PLUS, status `canceled`) needs exactly that most.
  // So it stays reachable on tier alone (the pre-D51f rule) and only the label follows standing: "Manage" for a
  // premium member, "Billing" for a lapsed one. (The API does not serve the Stripe customer id, so tier is the
  // only signal that a member has ever been a paying customer.)
  const hasBillingAccount = (subscription?.tier ?? SubscriptionTier.FREE) >= SubscriptionTier.PLUS;

  // Handle the return params Stripe's hosted pages redirect back with. Three
  // distinct returns, deliberately handled differently:
  // - `success=true`  checkout completed: celebratory toast + the convergence
  //   point (entitlements, current issue, archive, bounded webhook poll).
  // - `portal_return=true`  back from the Customer Portal: the SAME convergence
  //   point, but NO toast — the member may only have updated a card or
  //   cancelled, and "Welcome to Paire+!" would be wrong. Silent on purpose.
  // - `canceled=true`  checkout abandoned: an info toast and NO invalidation —
  //   nothing changed server-side, so there is nothing to refresh.
  // Each param is cleared from the URL once handled (replace, so Back does not
  // re-trigger it). The effect depends on the primitive param values, not the
  // `search` object, so an unrelated re-render never re-runs it; and a re-run
  // that does happen (React StrictMode's dev double-invoke) is harmless: the
  // toast has a fixed id so it can only be on screen once, and starting a
  // refresh supersedes the previous one (`refreshBillingState`).
  const { success, canceled, portal_return: portalReturn } = search;
  // One toast at a time, shared with the native success deep link: a still-visible earlier return toast is updated in
  // place, never stacked beside the new one.
  const showOnce = useBillingReturnToast();
  useEffect(() => {
    const kind = isTrueParam(success)
      ? 'success'
      : isTrueParam(portalReturn)
        ? 'portal'
        : isTrueParam(canceled)
          ? 'canceled'
          : null;
    if (!kind) return;

    if (kind === 'success') {
      showOnce({
        ...BILLING_SUCCESS_TOAST,
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      void invalidateSubscription();
    } else if (kind === 'portal') {
      void invalidateSubscription();
    } else {
      showOnce({
        ...BILLING_CANCELED_TOAST,
        status: 'info',
        duration: 3000,
        isClosable: true,
      });
    }
    // A failed navigation must not surface as an unhandled rejection; the refresh above has already started.
    void router.navigate({ to: '/settings/subscription', search: {}, replace: true }).catch(() => {});
  }, [success, canceled, portalReturn, showOnce, invalidateSubscription, router]);

  // Find plans for the selected tier
  const monthlyPlan = plans?.find(
    (p) => p.tier === selectedTier && p.interval === 'month'
  );
  const yearlyPlan = plans?.find(
    (p) => p.tier === selectedTier && p.interval === 'year'
  );

  const handleUpgrade = async (stripePriceId: string) => {
    try {
      const result = await checkoutMutation.mutateAsync({
        priceId: stripePriceId,
        idempotencyToken: getIdempotencyToken(stripePriceId),
        successUrl: `${window.location.origin}/settings/subscription?success=true`,
        cancelUrl: `${window.location.origin}/settings/subscription?canceled=true`,
      });
      await openExternalCheckout(result.checkoutUrl);
    } catch (error) {
      toast({
        title: 'Checkout failed',
        description: error instanceof Error ? error.message : 'Please try again',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleManageSubscription = async () => {
    try {
      const result = await portalMutation.mutateAsync(
        `${window.location.origin}/settings/subscription`
      );
      await openCustomerPortal(result.portalUrl);
    } catch (error) {
      toast({
        title: 'Failed to open portal',
        description: error instanceof Error ? error.message : 'Please try again',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  if (isLoading) {
    return (
      <PageWrapper>
        <SEO title="Subscription Settings" />
        <AppHeader page="settings" />
        <Box minH="100vh">
          <Container maxW="container.md" py={6}>
            <VStack spacing={4} align="stretch">
              <Skeleton height="60px" />
              <Skeleton height="200px" />
              <Skeleton height="200px" />
            </VStack>
          </Container>
        </Box>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      <SEO title="Subscription Settings" />
      <AppHeader page="settings" />
      <Box minH="100vh">
        <Container maxW="container.md" py={6}>
          <VStack spacing={6} align="stretch">
            {/* Header */}
            <HStack justify="space-between" align="center">
              <Button
                leftIcon={<ArrowLeft size={18} />}
                variant="ghost"
                onClick={() => router.history.back()}
                size={{ base: 'sm', lg: 'md' }}
                color="var(--canvas-text)"
                _hover={{ bg: 'var(--color-white-overlay-10)' }}
                _active={{ bg: 'var(--color-white-overlay-20)' }}
              >
                Back
              </Button>
              <Heading size="lg" color="var(--canvas-text)">
                <HStack spacing={2}>
                  <Crown size={28} />
                  <Text>Subscription</Text>
                </HStack>
              </Heading>
              <Box w="60px" />
            </HStack>

            {/* Current Plan Status */}
            <Box
              p={6}
              borderRadius="var(--r-card)"
              bg={isPremium ? 'purple.900' : 'var(--card-bg)'}
              border="1px solid"
              borderColor={isPremium ? 'purple.500' : 'var(--card-divider)'}
              boxShadow={isPremium ? undefined : 'var(--card-shadow)'}
            >
              <HStack justify="space-between" align="start">
                <VStack align="start" spacing={2}>
                  <HStack>
                    <Text fontSize="sm" color={isPremium ? 'whiteAlpha.700' : 'var(--card-text-2)'} textTransform="uppercase">
                      Current Plan
                    </Text>
                    <Badge
                      colorScheme={isPremium ? 'purple' : 'gray'}
                      fontSize="xs"
                    >
                      {subscription?.status || 'active'}
                    </Badge>
                  </HStack>
                  <HStack spacing={3}>
                    {isPremium && <Icon as={Sparkles} color="purple.400" />}
                    <Text fontSize="display" fontWeight="bold" color={isPremium ? 'white' : 'var(--card-text)'}>
                      {getTierDisplayName(subscription?.tier ?? SubscriptionTier.FREE)}
                    </Text>
                  </HStack>
                  {subscription?.currentPeriodEnd && isPremium && (
                    <Text fontSize="sm" color="whiteAlpha.700">
                      Renews on{' '}
                      {new Date(subscription.currentPeriodEnd).toLocaleDateString()}
                    </Text>
                  )}
                </VStack>

                {hasBillingAccount && (
                  <Button
                    size="sm"
                    variant="outline"
                    // A lapsed member sits on the light card, where the premium card's purple-on-dark styling
                    // would be low-contrast: neutral card tokens instead.
                    borderColor={isPremium ? 'purple.500' : 'var(--card-divider)'}
                    color={isPremium ? 'purple.400' : 'var(--card-text)'}
                    _hover={{ bg: isPremium ? 'purple.900' : 'var(--card-divider)' }}
                    onClick={handleManageSubscription}
                    isLoading={portalMutation.isPending}
                    rightIcon={<ExternalLink size={14} />}
                  >
                    {isPremium ? 'Manage' : 'Billing'}
                  </Button>
                )}
              </HStack>
            </Box>

            {/* Past Due Warning */}
            {subscription?.status === 'past_due' && (
              <Alert status="warning" borderRadius="lg">
                <AlertIcon />
                <Box>
                  <AlertTitle>Payment Issue</AlertTitle>
                  <AlertDescription>
                    We had trouble processing your last payment. Your Paire+/Pro
                    features are still active while we retry — please check your
                    payment method to avoid any interruption.
                  </AlertDescription>
                </Box>
                <Button
                  size="sm"
                  colorScheme="orange"
                  ml="auto"
                  onClick={handleManageSubscription}
                >
                  Update Payment
                </Button>
              </Alert>
            )}

            {/* Upgrade Options (for free users) */}
            {!isPremium && (
              <Box
                p={6}
                borderRadius="var(--r-card)"
                bg="var(--card-bg)"
                border="var(--card-border)"
                boxShadow="var(--card-shadow)"
              >
                <VStack spacing={4} align="stretch">
                  <HStack>
                    <Icon as={Crown} color="purple.400" boxSize={6} />
                    <Text fontSize="xl" fontWeight="bold" color="var(--card-text)">
                      Upgrade to Paire+
                    </Text>
                  </HStack>

                  <Text color="var(--card-text-2)">
                    Unlock the full Paire experience with AI-powered insights,
                    unlimited lists, and advanced group features.
                  </Text>

                  <HStack spacing={2}>
                    <Button
                      size="sm"
                      flex={1}
                      variant={selectedTier === SubscriptionTier.PLUS ? 'solid' : 'outline'}
                      colorScheme="purple"
                      onClick={() => setSelectedTier(SubscriptionTier.PLUS)}
                    >
                      Paire+
                    </Button>
                    <Button
                      size="sm"
                      flex={1}
                      variant={selectedTier === SubscriptionTier.PRO ? 'solid' : 'outline'}
                      colorScheme="purple"
                      onClick={() => setSelectedTier(SubscriptionTier.PRO)}
                    >
                      Pro
                    </Button>
                  </HStack>

                  <Box bg="var(--card-surface-2)" p={4} borderRadius="var(--r-card-compact)">
                    <Text fontSize="sm" fontWeight="medium" color="var(--card-text-2)" mb={3}>
                      PAIRE+ AND PRO INCLUDE:
                    </Text>
                    <VStack spacing={2} align="stretch">
                      {PLUS_FEATURES.map((feature, i) => (
                        <HStack key={i} spacing={3}>
                          <Icon as={Check} color="purple.400" boxSize={4} />
                          <Text fontSize="sm" color="var(--card-text)">
                            {feature}
                          </Text>
                        </HStack>
                      ))}
                    </VStack>
                  </Box>

                  <Divider borderColor="var(--card-divider)" />

                  <VStack spacing={3}>
                    {monthlyPlan && (
                      <Button
                        width="100%"
                        size="lg"
                        bg="purple.500"
                        color="white"
                        _hover={{ bg: 'purple.600' }}
                        onClick={() => handleUpgrade(monthlyPlan.stripePriceId)}
                        isLoading={checkoutMutation.isPending}
                        leftIcon={<Crown size={20} />}
                      >
                        Get {getTierDisplayName(selectedTier)} - ${monthlyPlan.priceUsd}/month
                      </Button>
                    )}

                    {yearlyPlan && (
                      <Button
                        width="100%"
                        size="md"
                        variant="outline"
                        borderColor="purple.500"
                        color="purple.400"
                        _hover={{ bg: 'purple.900' }}
                        onClick={() => handleUpgrade(yearlyPlan.stripePriceId)}
                        isLoading={checkoutMutation.isPending}
                      >
                        Get yearly - ${yearlyPlan.priceUsd}/year (save 33%)
                      </Button>
                    )}
                  </VStack>

                  {/* D66 contrast sweep: --card-text-3 (#8A8A8A light / #847F94 dark)
                      measures only ~3.45:1 / ~3.86:1 against --card-bg for normal-size
                      text — short of WCAG's 4.5:1 floor in both modes (confirmed via the
                      sweep's axe measurement, not a false positive: this Text sits on a
                      real, opaque `var(--card-bg)` ancestor at :274, not a translucent
                      surface). --card-text-2 (#6B6B6B / #ADAAB8) clears 4.5:1 in both
                      modes and is already used elsewhere in this file for body copy —
                      reused rather than introducing a new token. */}
                  <Text fontSize="xs" color="var(--card-text-2)" textAlign="center">
                    Cancel anytime. Secure checkout via Stripe.
                  </Text>
                </VStack>
              </Box>
            )}

            {/* Current Plan Features */}
            <Box
              p={6}
              borderRadius="var(--r-card)"
              bg="var(--card-bg)"
              border="var(--card-border)"
              boxShadow="var(--card-shadow)"
            >
              <VStack spacing={4} align="stretch">
                <Text fontSize="lg" fontWeight="bold" color="var(--card-text)">
                  {isPremium ? 'Your Paire+ Benefits' : 'Your Free Plan'}
                </Text>

                <VStack spacing={2} align="stretch">
                  {(isPremium ? PLUS_FEATURES : FREE_FEATURES).map(
                    (feature, i) => (
                      <HStack key={i} spacing={3}>
                        <Icon
                          as={Check}
                          color={isPremium ? 'purple.400' : 'green.400'}
                          boxSize={4}
                        />
                        <Text fontSize="sm" color="var(--card-text)">
                          {feature}
                        </Text>
                      </HStack>
                    )
                  )}
                </VStack>
              </VStack>
            </Box>

            {/* Refresh Button */}
            <Button
              variant="ghost"
              size="sm"
              leftIcon={<RefreshCw size={16} />}
              onClick={() => invalidateSubscription()}
              color="var(--canvas-text-2)"
              _hover={{ color: 'var(--canvas-text)', bg: 'var(--color-white-overlay-10)' }}
              _active={{ bg: 'var(--color-white-overlay-20)' }}
            >
              Refresh subscription status
            </Button>
          </VStack>
        </Container>
      </Box>
    </PageWrapper>
  );
}
